import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLocationPath } from '@/hooks/useLocationNavigate';
import {
  X,
  LayoutDashboard,
  Users,
  Calendar,
  BarChart3,
  Settings,
  Shield,
  GraduationCap,
  LogOut,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuth } from '@/contexts/AuthContext';
import type { UserRole } from 'shared';

interface NavItem {
  icon: LucideIcon;
  path: string;
  label: string;
  roles?: UserRole[];
}

const navItems: NavItem[] = [
  { icon: LayoutDashboard, path: '/', label: 'Übersicht' },
  { icon: BarChart3, path: '/statistics', label: 'Statistiken', roles: ['super_admin', 'location_admin'] },
  { icon: Users, path: '/contacts', label: 'Kontakte' },
  { icon: Calendar, path: '/calendar', label: 'Kalender' },
  { icon: GraduationCap, path: '/schulung', label: 'Schulung' },
  { icon: Shield, path: '/admin', label: 'Administration', roles: ['super_admin', 'location_admin'] },
  { icon: Settings, path: '/settings', label: 'Einstellungen' },
];

export interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileNav({ isOpen, onClose }: MobileNavProps) {
  const location = useLocation();
  const locationPath = useLocationPath();
  const { data: currentUser } = useCurrentUser();
  const { logout } = useAuth();

  // Filter nav items based on user role
  const visibleNavItems = navItems.filter(item => {
    if (!item.roles) return true;
    return currentUser && item.roles.includes(currentUser.role);
  });

  // Get user initials
  const userInitials = currentUser
    ? `${currentUser.firstName?.[0] || ''}${currentUser.lastName?.[0] || ''}`
    : 'U';

  // Close nav on route change
  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  // Prevent body scroll when nav is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 w-64 bg-slate-800 z-50 md:hidden transform transition-transform',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Header */}
        <div className="h-16 px-4 flex items-center justify-between border-b border-slate-700">
          <div className="flex items-center gap-3">
            <img
              src="/fav.png"
              alt="vit:bikes"
              className="w-10 h-10 object-contain"
            />
            <span className="font-semibold text-slate-100">vit:bikes</span>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:bg-slate-700 hover:text-slate-200 transition-colors"
            aria-label="Menü schließen"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="py-4">
          <ul className="space-y-1 px-2">
            {visibleNavItems.map((item) => {
              const subPath = location.pathname.replace(/^\/location\/[^/]+/, '') || '/';
              const isActive = subPath === item.path ||
                (item.path !== '/' && subPath.startsWith(item.path));

              return (
                <li key={item.path}>
                  <Link
                    to={locationPath(item.path)}
                    className={cn(
                      'flex items-center gap-3 px-3 py-3 rounded-lg transition-colors',
                      isActive
                        ? 'bg-slate-700 text-orange-500'
                        : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-200'
                    )}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-linear-to-br from-blue-500 to-purple-500 flex items-center justify-center">
              <span className="text-white font-semibold text-sm">{userInitials}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-200 truncate">
                {currentUser?.firstName} {currentUser?.lastName}
              </p>
              <p className="text-xs text-slate-400 truncate">{currentUser?.email}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-slate-400 hover:bg-slate-700 hover:text-red-400 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm">Abmelden</span>
          </button>
        </div>
      </aside>
    </>
  );
}
